import type { CSSProperties } from 'react'
import type { ShareConfig } from 'src/stores/components/common/search'
import type { SearchEvtData } from 'src/components/common/declare'
import type { FilterGroup } from 'src/utils/common'
import React, { useEffect } from 'react'
import { when, reaction, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Form, Button, Popover } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { LoadableControl } from 'src/components'
import { ListSeatchExpand } from './components'
import { appStore, searchStore, listStore } from 'src/stores'
import { $api, $consts, $evt } from 'src/plugins'
import { useLocalSearch } from 'src/hooks'
import { initFilter } from 'src/utils/common'
import { getDefaultValueFrom_DATA_VALUE, getFilterGroupFrom_Nav_FormQuery } from 'src/components/root/helper'
import { uuid } from 'src/utils/tools'
import { set, pick, cloneDeep, isEmpty } from 'lodash'
import 'src/styles/components/base/listSearch.less'

type Props = {
  formCode: string,
  preventAutoConfig?: boolean,
  preventAutoData?: boolean,
  onRef?: Function,
  onSearch?: Function,
  className?: string,
  style?: CSSProperties
}

const ListSearchContext = React.createContext<any>(undefined)
const ListSearch = (props: Props) => {
  const queryParams = useLocalSearch()

  const state = useLocalObservable(() => {
    return {
      // ListSearch 组件实例 key
      insKey: uuid(),
      // formCode 对应表单的搜索配置（原始配置）
      initConfig: {} as any,
      // 共享以及记忆存储配置
      shareConfig: {
        // [storageKey: string]: {
        //   shareKey: string,
        //   isRemember?: boolean,
        //   queryFields?: string[]
        // }
      } as ShareConfig,
      get shareKeys () {
        return Object.keys(this.shareConfig).map(key => this.shareConfig[key].shareKey)
      },
      get shareData () {
        const shareData: any = {}
        this.shareKeys.forEach(key => {
          Object.assign(shareData, toJS(searchStore.shareBase[key]))
        })
        return shareData
      },
      // 配置了默认值，但需要通过接口获取实际默认值的组件，需要通过 Promise 来保持
      queryKeeping: {} as any,
      // formCode 对应表单的搜索配置（过滤后的配置）
      searchConfig: {} as any,
      setSearchConfig (config: any) {
        // Promise 保持函数
        const keepingFunc = []
        // 默认搜索条件
        const presetForm: any = {}
        // 共享以及记忆存储
        const shareConfig: ShareConfig = {}
        // 存储原始配置
        this.initConfig = cloneDeep(config)
        // 过滤后配置，IS_DEFAULT_QUERY 需要显示的搜索条件
        const { Nav_FormQuery = [] } = config
        Nav_FormQuery.sort((a: any, b: any) => a.NUM - b.NUM)
        Nav_FormQuery.forEach((item: any) => {
          item.Nav_Querys = item.Nav_Querys?.filter((query: any) => query.IS_DEFAULT_QUERY)
          item.Nav_Querys?.sort((a: any, b: any) => a.NUM - b.NUM)
          // 记忆存储以 Nav_FormQuery 的具体 item 为单元，支持 Nav_FormQuery 中只有某个 item 设置了 IS_REMEMBER
          const queryFields: string[] = []
          // 默认搜索条件
          item.Nav_Querys?.forEach((query: any) => {
            queryFields.push(query.QUERY_NAME)
            if (query.DATA_VALUE !== undefined) {
              Object.assign(presetForm, { [query.QUERY_NAME]: getDefaultValueFrom_DATA_VALUE(query.DATA_VALUE, query) })
              // 组件配置了默认值，但需要接口请求以获取到实际默认值时，推入 Promise 队列，执行完获取到实际默认值时，再继续执行查询操作
              switch (query.DATA_TYPE) {
                case $consts['TABLE/DATA_TYPE_PAGINATION_SELECT']:
                  keepingFunc.push(new Promise(resolve => {
                    this.queryKeeping[query.QUERY_NAME] = resolve
                  }))
                  break
                default:
                  break
              }
            }
            // 从浏览器链接取默认值
            else if (queryParams[query.QUERY_NAME] !== undefined) {
              Object.assign(presetForm, { [query.QUERY_NAME]: queryParams[query.QUERY_NAME] })
            }
          })
          // 共享以及记忆存储
          const { ID, SHARE_KEY, IS_REMEMBER } = item
          SHARE_KEY && set(shareConfig, ID, { shareKey: SHARE_KEY, isRemember: !!IS_REMEMBER, queryFields })
        })
        // 过滤后配置
        this.searchConfig = config
        // 默认搜索条件
        this.pureSetFormBase(presetForm)
        // 共享以及记忆存储
        this.shareConfig = shareConfig
        // 返回 Promise 以保证取到本地记忆存储的内容后再调用搜索
        keepingFunc.push(searchStore.registerShareBase(shareConfig))
        return Promise.all(keepingFunc)
      },
      // 搜索条件
      formBase: {} as any,
      setFormBase (data: any) {
        // 保存共享以及记忆
        for (const [key, value] of Object.entries(this.shareConfig)) {
          const { shareKey, isRemember, queryFields } = value
          const queryData = pick(data, queryFields!)
          if (Object.keys(queryData).length) {
            searchStore.setShareBase(shareKey, queryData, isRemember ? key : undefined)
          }
        }
        Object.assign(this.formBase, data)
      },
      // 只设置 formBase 不做其他操作
      pureSetFormBase (data: any) {
        Object.assign(this.formBase, data)
      },
      // 分组搜索默认搜索面板
      activeKey: '',
      setActiveKey (key: string) {
        this.activeKey = key
      },
      // 当前激活面板搜索配置
      get activeFormQuery () {
        const { Nav_FormQuery = [] } = this.searchConfig
        return Nav_FormQuery.find((item: any) => item.ID === this.activeKey)
      },
      // 默认显示搜索项数量
      get showCount () {
        return this.activeFormQuery?.SHOW_COUNT || $consts['TABLE/LIST_SEARCH_SIMPLE_SEARCH_COUNTS']
      },
      // 默认显示的搜索项
      get simpleSearch () {
        return this.activeFormQuery?.Nav_Querys?.slice(0, this.showCount) || []
      },
      // 展开显示的搜索项
      get expandSearch () {
        return this.activeFormQuery?.Nav_Querys?.slice(this.showCount) || []
      },
      // 是否显示展开按钮
      get expandVisible () {
        const { Nav_FormQuery = [] } = this.searchConfig
        return Nav_FormQuery.length > 1 || this.expandSearch.length
      },
      /**
       * 执行搜索
       * @param config 搜索配置
       * @param config.emit 是否广播此次搜索，以同步共享搜索组件
       */
      search (config?: { emit?: boolean }) {
        listStore.table.get(props.formCode)?.search()
        props.onSearch?.(this.getSearchFilterGroup())
        if (config?.emit && !isEmpty(this.shareConfig)) {
          $evt.emit($consts['EVT/SEARCH'], {
            insKey: this.insKey,
            shareKeys: this.shareKeys
          })
        }
      },
      // 获取当前激活搜索面板的搜索过滤条件
      getSearchFilterGroup () {
        // 使用 initConfig 是因为要获取那些配置了条件但又不显示的搜索项
        const { Nav_FormQuery = [] } = this.initConfig
        const querys = Nav_FormQuery.find((item: any) => item.ID === this.activeKey)?.Nav_Querys || []
        const FilterGroup: FilterGroup = { Groups: [], Rules: [] }
        querys.forEach((query: any) => {
          // 默认显示的搜索条件取 formBase 的输入值，非默认显示的搜索项，取配置的默认值进行搜索
          const value = query.IS_DEFAULT_QUERY ? this.formBase[query.QUERY_NAME] : getDefaultValueFrom_DATA_VALUE(query.DATA_VALUE, query)
          /**
           * 控件附加值，优先于 控件选中值
           * 比如：分页下拉组件配置了导航属性，则需要将导航属性用作搜索参数，而不是 控件选中值
           * {
           *   Groups: { field1: value1, field2: value2 },
           *   Rules: { field1: value1, field2: value2 }
           * }
           */
          const extra = this.formBase[`${$consts['TABLE/LIST_SEARCH_EXTRA_DATA_PREFIX']}${query.QUERY_NAME}`]
          if (value !== undefined || extra !== undefined) {
            if (extra) {
              // 填充 Groups
              if (extra?.Groups) {
                FilterGroup.Groups!.push({ IsAnd: false, Groups: [], Rules: [] })
                for (const [key, value] of Object.entries(extra.Groups)) {
                  value !== undefined && FilterGroup.Groups![0].Rules.push({
                    Field: key,
                    Operate: '1',
                    Value: value,
                    IsCustom: query.IS_CUSTOM,
                    IsSysParamRule: query.IS_SYS_PARAM
                  })
                }
              }
              // 填充 Rules
              if (extra?.Rules) {
                for (const [key, value] of Object.entries(extra.Rules)) {
                  value !== undefined && FilterGroup.Rules!.push({
                    Field: key,
                    Operate: '1',
                    Value: value,
                    IsCustom: query.IS_CUSTOM,
                    IsSysParamRule: query.IS_SYS_PARAM
                  })
                }
              }
            } else {
              const filterGroup = getFilterGroupFrom_Nav_FormQuery(value, query)
              filterGroup.Groups?.length && FilterGroup.Groups!.push(...filterGroup.Groups)
              filterGroup.Rules?.length && FilterGroup.Rules!.push(...filterGroup.Rules)
            }
          }
        })
        return FilterGroup
      },
    }
  })

  useEffect(() => {
    searchStore.search.set(props.formCode, state)
    // 直接通过 onRef 暴露 ListTable 的 Context
    props.onRef instanceof Function && props.onRef(state)
  }, [])

  // 获取搜索配置
  useEffect(() => {
    if (props.preventAutoConfig) return

    const { formCode } = props

    /**
     * formCode 子表传过来的是 PF001===record.ID（用于唯一标识子表，因为子表和父表是同一个 formCode）
     * 因此通过 split('===')[0] 取到正确的 formCode
     */
    const loadDataKey = formCode.split('===')[0]

    const { OrgId } = appStore.current.headers
    $api['table/getTableConfig'](
      { OrgId, Key: loadDataKey },
      { noErrorToast: true }
    ).catch(() => {
      // 旧版本接口兼容
      const payload = initFilter({ OrgId, Keyword: loadDataKey })
      return $api['table/getSearchConfigInfo'](payload)
    }).then((config: any) => {
      return state.setSearchConfig(config).finally(() => {
        $evt.emit($consts['LIFECYCLE/LIST_SEARCH_CONFIG_LOADED'], { formCode: props.formCode, data: config })
      })
    }).finally(() => {
      !props.preventAutoData && state.search()
    })
  }, [props.formCode])

  // 共享以及记忆存储配置
  useEffect(() => reaction(
    () => toJS(state.shareData),
    shareData => state.pureSetFormBase(shareData)
  ), [])

  // 共享搜索响应条件变化并主动发起搜索
  useEffect(() => {
    const handleEmitSearch = (params: SearchEvtData) => {
      if (!isEmpty(state.shareConfig)) {
        const { insKey: emitInsKey, shareKeys: emitShareKeys } = params
        if (emitShareKeys.filter(key => state.shareKeys.includes(key)).length && emitInsKey !== state.insKey) {
          state.search()
        }
      }
    }

    $evt.addListener($consts['EVT/SEARCH'], handleEmitSearch)

    return () => {
      $evt.removeListener($consts['EVT/SEARCH'], handleEmitSearch)
    }
  }, [])

  // 分组搜索默认搜索面板
  useEffect(() => when(
    () => !!state.searchConfig.Nav_FormQuery?.[0]?.ID,
    () => state.setActiveKey(state.searchConfig.Nav_FormQuery[0].ID)
  ), [])

  if (!state.activeKey) return <div />

  return (
    <ListSearchContext.Provider value={state}>
      <Form layout='inline' className={`list-search ${props.className}`} style={props.style}>
        {
          state.simpleSearch.map((item: any, index: number) => (
            <LoadableControl
              key={`${item.ID}_${index}`}
              pageType={$consts['TABLE/FORM_TYPE_LIST_PAGE']}
              locateControl={$consts['TABLE/CONTROL_LOCATE_HEADER']}
              config={item}
            />
          ))
        }
        {
          state.expandVisible
            ? (
              <Form.Item>
                <Popover
                  placement='bottom'
                  trigger='click'
                  content={<ListSeatchExpand />}
                >
                  <div className='list-search__expand'>
                    <span>{state.activeFormQuery.LABEL || '更多'}</span>
                    <MoreOutlined className='fz-18' />
                  </div>
                </Popover>
              </Form.Item>
            )
            : null
        }
        <Form.Item>
          <Button type='primary' onClick={() => state.search({ emit: true })}>查询</Button>
        </Form.Item>
      </Form>
    </ListSearchContext.Provider>
  )
}

export { ListSearchContext }
export default observer(ListSearch)